import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export type CountryCode =
  | "AC"
  | "AD"
  | "AE"
  | "AF"
  | "AG"
  | "AI"
  | "AL"
  | "AM"
  | "AO"
  | "AR"
  | "AS"
  | "AT"
  | "AU"
  | "AW"
  | "AX"
  | "AZ"
  | "BA"
  | "BB"
  | "BD"
  | "BE"
  | "BF"
  | "BG"
  | "BH"
  | "BI"
  | "BJ"
  | "BL"
  | "BM"
  | "BN"
  | "BO"
  | "BQ"
  | "BR"
  | "BS"
  | "BT"
  | "BW"
  | "BY"
  | "BZ"
  | "CA"
  | "CC"
  | "CD"
  | "CF"
  | "CG"
  | "CH"
  | "CI"
  | "CK"
  | "CL"
  | "CM"
  | "CN"
  | "CO"
  | "CR"
  | "CU"
  | "CV"
  | "CW"
  | "CX"
  | "CY"
  | "CZ"
  | "DE"
  | "DJ"
  | "DK"
  | "DM"
  | "DO"
  | "DZ"
  | "EC"
  | "EE"
  | "EG"
  | "EH"
  | "ER"
  | "ES"
  | "ET"
  | "FI"
  | "FJ"
  | "FK"
  | "FM"
  | "FO"
  | "FR"
  | "GA"
  | "GB"
  | "GD"
  | "GE"
  | "GF"
  | "GG"
  | "GH"
  | "GI"
  | "GL"
  | "GM"
  | "GN"
  | "GP"
  | "GQ"
  | "GR"
  | "GT"
  | "GU"
  | "GW"
  | "GY"
  | "HK"
  | "HN"
  | "HR"
  | "HT"
  | "HU"
  | "ID"
  | "IE"
  | "IL"
  | "IM"
  | "IN"
  | "IO"
  | "IQ"
  | "IR"
  | "IS"
  | "IT"
  | "JE"
  | "JM"
  | "JO"
  | "JP"
  | "KE"
  | "KG"
  | "KH"
  | "KI"
  | "KM"
  | "KN"
  | "KP"
  | "KR"
  | "KW"
  | "KY"
  | "KZ"
  | "LA"
  | "LB"
  | "LC"
  | "LI"
  | "LK"
  | "LR"
  | "LS"
  | "LT"
  | "LU"
  | "LV"
  | "LY"
  | "MA"
  | "MC"
  | "MD"
  | "ME"
  | "MF"
  | "MG"
  | "MH"
  | "MK"
  | "ML"
  | "MM"
  | "MN"
  | "MO"
  | "MP"
  | "MQ"
  | "MR"
  | "MS"
  | "MT"
  | "MU"
  | "MV"
  | "MW"
  | "MX"
  | "MY"
  | "MZ"
  | "NA"
  | "NC"
  | "NE"
  | "NF"
  | "NG"
  | "NI"
  | "NL"
  | "NO"
  | "NP"
  | "NR"
  | "NU"
  | "NZ"
  | "OM"
  | "PA"
  | "PE"
  | "PF"
  | "PG"
  | "PH"
  | "PK"
  | "PL"
  | "PM"
  | "PR"
  | "PS"
  | "PT"
  | "PW"
  | "PY"
  | "QA"
  | "RE"
  | "RO"
  | "RS"
  | "RU"
  | "RW"
  | "SA"
  | "SB"
  | "SC"
  | "SD"
  | "SE"
  | "SG"
  | "SH"
  | "SI"
  | "SJ"
  | "SK"
  | "SL"
  | "SM"
  | "SN"
  | "SO"
  | "SR"
  | "SS"
  | "ST"
  | "SV"
  | "SX"
  | "SY"
  | "SZ"
  | "TA"
  | "TC"
  | "TD"
  | "TG"
  | "TH"
  | "TJ"
  | "TK"
  | "TL"
  | "TM"
  | "TN"
  | "TO"
  | "TR"
  | "TT"
  | "TV"
  | "TW"
  | "TZ"
  | "UA"
  | "UG"
  | "US"
  | "UY"
  | "UZ"
  | "VA"
  | "VC"
  | "VE"
  | "VG"
  | "VI"
  | "VN"
  | "VU"
  | "WF"
  | "WS"
  | "XK"
  | "YE"
  | "YT"
  | "ZA"
  | "ZM"
  | "ZW";

export function formatGermanNumberWithoutDecimals(value: number): string {
  const intPart = Math.floor(value).toString();
  return intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
}

export function convertCmToMeters(value: number | null | undefined): number {
  return value ? value / 100 : 0;
}

/**
 * Named presets for commonly used image sizes.
 * Centralizing them here allows quick global tuning.
 */
export const IMAGE_SIZES = {
  cardThumb: "400x300",
  galleryLarge: "1600x900",
  galleryThumb: "600x400",
  hero: "1920x1080",
  logo: "300x150",
  avatar: "200x200",
  mapThumb: "300x200",
  contentImage: "800x600",
} as const;

export type ImageSizeKey = keyof typeof IMAGE_SIZES;

/**
 * Resolve a file/image URL consistently across the app.
 *
 * Rules:
 * - Empty/whitespace-only input → empty string
 * - Starts with http:// or https:// → returned as-is
 * - Otherwise:
 *   - If VITE_ASSET_URL is set: base (without trailing slash) + "/" + path (unless path already starts with "/")
 *   - If VITE_ASSET_URL is not set: returned as-is (relative URL)
 */
export function resolveAssetUrl(path?: string | null): string {
  const raw = path?.trim() ?? "";
  if (!raw) return "";

  // Absolute HTTP(S) URL → use as-is
  if (/^https?:\/\//i.test(raw)) {
    return raw;
  }

  const baseEnv = import.meta.env.VITE_ASSET_URL ?? "";
  if (!baseEnv) {
    // Fall back to existing relative behavior when no base is configured
    return raw;
  }

  const base = baseEnv.replace(/\/$/, "");
  const nRaw = raw.startsWith("fileadmin") ? raw.slice(9) : raw;
  return `${base}${nRaw.startsWith("/") ? nRaw : `/${nRaw}`}`;
}

type ImageUrlOptions =
  | {
      size?: string | ImageSizeKey;
      format?: string;
    }
  | undefined;

/**
 * Cache-bust version for Cabby-transformed image URLs (`?size=&format=`).
 * Requires non-empty `VITE_ASSET_CACHE_VERSION`. Bump it after Cabby
 * transform-cache invalidation so browsers/CDNs refetch (Cabby ignores `v`).
 *
 * When both `process.env` and `import.meta.env` define the value, they must
 * match — otherwise SSR and the client bundle can emit different image URLs.
 */
export function getAssetCacheVersion(): string {
  const normalize = (value: unknown): string | undefined => {
    if (value == null) return undefined;
    const trimmed = String(value).trim();
    return trimmed || undefined;
  };

  const fromProcess =
    typeof process !== "undefined"
      ? normalize(process.env?.VITE_ASSET_CACHE_VERSION)
      : undefined;
  const fromImport = normalize(import.meta.env?.VITE_ASSET_CACHE_VERSION);

  if (fromProcess && fromImport && fromProcess !== fromImport) {
    throw new Error(
      `VITE_ASSET_CACHE_VERSION mismatch: process.env="${fromProcess}" vs import.meta.env="${fromImport}"`,
    );
  }

  const version = fromProcess ?? fromImport;
  if (!version) {
    throw new Error(
      "VITE_ASSET_CACHE_VERSION must be set to a non-empty value",
    );
  }

  return version;
}

function appendImageParams(url: string, options: ImageUrlOptions): string {
  if (!options) return url;

  const sizeValue =
    options.size && options.size in IMAGE_SIZES
      ? IMAGE_SIZES[options.size as ImageSizeKey]
      : options.size;

  const formatValue = options.format ?? "webp";
  const cacheVersion = getAssetCacheVersion();

  try {
    const u = new URL(url);
    if (sizeValue) {
      u.searchParams.set("size", sizeValue);
    }
    if (formatValue) {
      u.searchParams.set("format", formatValue);
    }
    u.searchParams.set("v", cacheVersion);
    return u.toString();
  } catch {
    // If URL constructor fails (e.g. relative URL without base), fall back to naive concatenation.
    const hasQuery = url.includes("?");
    const params: string[] = [];
    if (sizeValue) params.push(`size=${encodeURIComponent(sizeValue)}`);
    if (formatValue) params.push(`format=${encodeURIComponent(formatValue)}`);
    params.push(`v=${encodeURIComponent(cacheVersion)}`);
    return `${url}${hasQuery ? "&" : "?"}${params.join("&")}`;
  }
}

/**
 * Build an image URL for client-side usage with optional size/format parameters.
 * This should be used only for image resources – non-image files (PDFs, docs, etc.)
 * should continue to use resolveAssetUrl directly so they are not resized or converted.
 *
 * Transformed URLs include `v` from {@link getAssetCacheVersion} for cache busting.
 */
export function buildImageUrl(
  path?: string | null,
  options?: ImageUrlOptions,
): string {
  const baseUrl = resolveAssetUrl(path);
  if (!baseUrl) return "";
  return appendImageParams(baseUrl, options);
}

/**
 * Resolve a file/image URL on the server (e.g. in loaders, head).
 * Uses process.env.VITE_ASSET_URL for SSR contexts where import.meta.env may not be available.
 */
export function resolveAssetUrlServer(path?: string | null): string {
  const raw = path?.trim() ?? "";
  if (!raw) return "";

  if (/^https?:\/\//i.test(raw)) {
    return raw;
  }

  const baseEnv = process.env.VITE_ASSET_URL ?? "";
  if (!baseEnv) {
    return raw;
  }

  const base = baseEnv.replace(/\/$/, "");
  const nRaw = raw.startsWith("fileadmin") ? raw.slice(9) : raw;
  return `${base}${nRaw.startsWith("/") ? nRaw : `/${nRaw}`}`;
}

/**
 * Server-side variant of buildImageUrl for loaders/meta functions.
 */
export function buildImageUrlServer(
  path?: string | null,
  options?: ImageUrlOptions,
): string {
  const baseUrl = resolveAssetUrlServer(path);
  if (!baseUrl) return "";
  return appendImageParams(baseUrl, options);
}

/**
 * Get the base URL for the site (for og:url, canonical links, etc.).
 * Works in both client (import.meta.env) and server (process.env) contexts.
 */
export function getBaseUrl(): string {
  if (typeof process !== "undefined" && process.env?.VITE_BASE_URL) {
    return process.env.VITE_BASE_URL.replace(/\/$/, "");
  }
  return (import.meta.env?.VITE_BASE_URL ?? "").replace(/\/$/, "");
}
